import React, { useMemo } from "react";
import { makeStyles, Tab, Tabs, IconButton, CardMedia, Box } from "@material-ui/core";
import { ArrowBackIos, ArrowForwardIos } from "@material-ui/icons";
import clsx from "clsx";
import { PlaceHolderImage } from "@roc/feature-comp-tool";

interface StyleProps {
  height: string;
  roundedBorders: boolean;
}

const useStyles = makeStyles((theme) => ({
  imgContainer: ({ height, roundedBorders }: StyleProps) => ({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "white",
    width: "100%",
    borderRadius: roundedBorders ? "20px" : "0",
    position: "relative",
    overflow: "hidden",
    height: height,
  }),
  noImageContainer: ({ height }: StyleProps) => ({
    width: "100%",
    height: height,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }),
  imgOutOfFocus: {
    position: "fixed",
    top: "-4000px",
    opacity: 0
  },
  img: ({ height, roundedBorders }: StyleProps) => ({
    height: height,
    maxWidth: "100%",
    display: "block",
    width: "100%",
    objectFit: "cover",
    borderRadius: roundedBorders ? "20px" : "0",
  }),
  tabsContainer: {
    position: "absolute",
    bottom: "20px",
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },
  tabs: {
    minHeight: 0,
    width: "95%",
  },
  imgTab: {
    minWidth: "30px",
    height: "8px",
    margin: "0 4px",
    borderRadius: "4px",
    minHeight: 0,
    padding: 0,
    transition: "background-color 0.3s",
  },
  active: {
    backgroundColor: theme.palette.common.white,
  },
  inactive: {
    backgroundColor: theme.palette.grey[400],
  },
  navigationButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.8)",
    },
    zIndex: 2,
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    minWidth: 0,
    cursor: "pointer",
  },
  leftButton: {
    left: "10px",
    paddingLeft: "8px",
  },
  rightButton: {
    right: "10px",
  },
}));

interface PropertyPhotoGalleryProps {
  images: any[];
  height: string;
  roundedBorders?: boolean;
  onClick?: () => void;
  propertyId: string;
}


function PropertyPhotosGallery({ propertyId, images, height, roundedBorders = false, onClick = () => {/**Nothing to do here */ } }: PropertyPhotoGalleryProps) {
  const classes = useStyles({ height, roundedBorders });
  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const goPrev = () => {
    setValue((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goNext = () => {
    setValue((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const imagesWithIndex = useMemo(() => {
    if (!images) {
      return [];
    }
    return images.map((image, index) => ({ ...image, key: index }));
  }, [images]);

  const onError = (event) => {
    event.target.src = PlaceHolderImage;
  };

  return (
    <div className={classes.imgContainer}>
      {!images || images.length === 0 ? (
        <Box className={classes.noImageContainer} onClick={onClick}>
          <p>No images available</p>
        </Box>
      ) : (
        <>
          {imagesWithIndex.map((image) => (
            <CardMedia
              component="img"
              key={`${propertyId}-${image.url}`}
              className={clsx(classes.img, image.key === value ? "" : classes.imgOutOfFocus)}
              image={image.url}
              onClick={onClick}
              onError={onError}
            />
          ))}

          <IconButton
            className={`${classes.navigationButton} ${classes.leftButton}`}
            onClick={goPrev}
          >
            <ArrowBackIos />
          </IconButton>
          <IconButton
            className={`${classes.navigationButton} ${classes.rightButton}`}
            onClick={goNext}
          >
            <ArrowForwardIos />
          </IconButton>

          <div className={classes.tabsContainer}>
            <Tabs
              value={value}
              onChange={handleChange}
              className={classes.tabs}
              TabIndicatorProps={{ style: { display: "none" } }}
              variant="fullWidth"
            >
              {images.map((image, index) => (
                <Tab
                  key={index}
                  disableRipple
                  className={`${classes.imgTab} ${value === index ? classes.active : classes.inactive}`}
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
          </div>
        </>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    "aria-controls": `image-tabpanel-${index}`,
  };
}

export default PropertyPhotosGallery;
